import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { inspectionsAPI } from '../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  CheckCircle2,
  Download,
  Printer,
  QrCode,
  FileText,
  Home,
  Loader2,
  AlertCircle,
  RefreshCw,
  Image,
} from 'lucide-react';
import { cn } from '../lib/utils';

const getRecordTypeLabel = (type) => {
  const labels = {
    'car': 'Light Vehicle',
    '3w': '3-Wheeler',
    'bus': 'Bus',
    'truck': 'Truck',
    'trailer': 'Trailer (CNG Tube)',
    'cng_storage': 'CNG Storage',
  };
  return labels[type] || type;
};

export default function CertificateActionsPage() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRenewal = searchParams.get('renewal') === 'true';
  
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ certificate: false, qrPdf: false, qrImage: false });

  useEffect(() => {
    loadInspection();
  }, [inspectionId]);

  const loadInspection = async () => {
    try {
      const response = await inspectionsAPI.getById(inspectionId);
      setInspection(response.data);
    } catch (err) {
      console.error('Failed to load inspection:', err);
      toast.error('Failed to load inspection details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    setDownloading(prev => ({ ...prev, certificate: true }));
    try {
      const response = await inspectionsAPI.getCertificate(inspectionId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-${inspection?.certificate_number || inspectionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded successfully');
    } catch (err) {
      console.error('Failed to download certificate:', err);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(prev => ({ ...prev, certificate: false }));
    }
  };

  const handlePrintCertificate = async () => {
    setDownloading(prev => ({ ...prev, certificate: true }));
    try {
      const response = await inspectionsAPI.getCertificate(inspectionId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (err) {
      console.error('Failed to print certificate:', err);
      toast.error('Failed to print certificate');
    } finally {
      setDownloading(prev => ({ ...prev, certificate: false }));
    }
  };

  const handleDownloadQR = async () => {
    setDownloading(prev => ({ ...prev, qrPdf: true }));
    try {
      const response = await inspectionsAPI.getQRSticker(inspectionId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-sticker-${inspection?.verification_number || inspectionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('QR Code PDF downloaded successfully');
    } catch (err) {
      console.error('Failed to download QR code:', err);
      toast.error('Failed to download QR code');
    } finally {
      setDownloading(prev => ({ ...prev, qrPdf: false }));
    }
  };

  const handleDownloadQRImage = async () => {
    setDownloading(prev => ({ ...prev, qrImage: true }));
    try {
      const response = await inspectionsAPI.getQRImage(inspectionId);
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-code-${inspection?.verification_number || inspectionId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('QR Code image downloaded successfully');
    } catch (err) {
      console.error('Failed to download QR image:', err);
      toast.error('Failed to download QR image');
    } finally {
      setDownloading(prev => ({ ...prev, qrImage: false }));
    }
  };

  const handlePrintQR = async () => {
    setDownloading(prev => ({ ...prev, qrPdf: true }));
    try {
      const response = await inspectionsAPI.getQRSticker(inspectionId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (err) {
      console.error('Failed to print QR code:', err);
      toast.error('Failed to print QR code');
    } finally {
      setDownloading(prev => ({ ...prev, qrPdf: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="h-6 w-6" />
              <div>
                <p className="font-semibold">Inspection Not Found</p>
                <p className="text-sm">The inspection you're looking for doesn't exist or has been deleted.</p>
              </div>
            </div>
            <Button className="mt-4" onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6" data-testid="certificate-actions-page">
      {/* Success Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-emerald-100 mx-auto">
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-emerald-700">
            {isRenewal ? 'Inspection Renewed Successfully!' : 'Inspection Submitted Successfully!'}
          </h1>
          <p className="text-muted-foreground mt-1">
            The inspection has been saved and the certificate is ready
          </p>
        </div>
      </div>

      {/* Inspection Summary Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              {isRenewal ? (
                <RefreshCw className="h-5 w-5 text-blue-600" />
              ) : (
                <FileText className="h-5 w-5 text-primary" />
              )}
              {isRenewal ? 'Renewal Details' : 'Inspection Details'}
            </CardTitle>
            <Badge variant="default" className="bg-emerald-600">
              {inspection.status?.toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Registration/Serial</p>
              <p className="font-mono font-bold">
                {inspection.registration_number || inspection.storage_serial_number || '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Type</p>
              <p className="font-medium">{getRecordTypeLabel(inspection.record_type)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Certificate #</p>
              <p className="font-mono">{inspection.certificate_number || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Verification #</p>
              <p className="font-mono">{inspection.verification_number || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Valid From</p>
              <p>{inspection.valid_from ? format(new Date(inspection.valid_from), 'dd MMM yyyy') : '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Valid Until</p>
              <p className="font-semibold text-emerald-600">
                {inspection.valid_to ? format(new Date(inspection.valid_to), 'dd MMM yyyy') : '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Certificate Actions Card */}
      <Card className="border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Inspection Certificate
          </CardTitle>
          <CardDescription>
            Download or print the official inspection certificate
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleDownloadCertificate}
              disabled={downloading.certificate}
              className="flex-1 min-w-[140px]"
              data-testid="download-certificate-btn"
            >
              {downloading.certificate ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Download PDF
            </Button>
            <Button
              variant="outline"
              onClick={handlePrintCertificate}
              disabled={downloading.certificate}
              className="flex-1 min-w-[140px]"
              data-testid="print-certificate-btn"
            >
              {downloading.certificate ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Printer className="mr-2 h-4 w-4" />
              )}
              Print Certificate
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* QR Code Actions Card */}
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-blue-600" />
            QR Code Sticker
          </CardTitle>
          <CardDescription>
            Download the QR code as PDF (with sticker layout) or as an image file
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Download Options */}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="secondary"
              onClick={handleDownloadQR}
              disabled={downloading.qrPdf}
              className="flex-1 min-w-[140px]"
              data-testid="download-qr-pdf-btn"
            >
              {downloading.qrPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              Download as PDF
            </Button>
            <Button
              variant="secondary"
              onClick={handleDownloadQRImage}
              disabled={downloading.qrImage}
              className="flex-1 min-w-[140px]"
              data-testid="download-qr-image-btn"
            >
              {downloading.qrImage ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Image className="mr-2 h-4 w-4" />
              )}
              Download as Image
            </Button>
          </div>
          {/* Print Option */}
          <div>
            <Button
              variant="outline"
              onClick={handlePrintQR}
              disabled={downloading.qrPdf}
              className="w-full"
              data-testid="print-qr-btn"
            >
              {downloading.qrPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Printer className="mr-2 h-4 w-4" />
              )}
              Print QR Sticker
            </Button>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Navigation */}
      <div className="flex justify-center">
        <Button
          size="lg"
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="min-w-[200px]"
          data-testid="return-dashboard-btn"
        >
          <Home className="mr-2 h-5 w-5" />
          Return to Dashboard
        </Button>
      </div>
    </div>
  );
}
